import { faCalculator, faRuler } from '@fortawesome/pro-regular-svg-icons'
import {
  SectionWrapper,
  isNonEmptyNumber,
  numberFormatWithPrecision,
} from '@labrav/common'
import { useStyles } from './ChannelOfferDimensionDetails.styles'
import { StandardCustomDimensions } from '../../../../common/components/StandardCustomDimensions/StandardCustomDimensions'
import type { ReceivedResellerAuthorization } from '../../../../modules/receivedResellerAuthorization/reducer'
import { ContractDurationType, DiscountType } from '../../../../types/flyout'
import { get } from 'lodash'
import type { ChannelPartnerPrivateOffer } from '../../../../modules/channelPartnerPrivateOffer/reducer'
import {
  ChannelPartnerPrivateOfferInCreation,
  CppoPriceAdjustmentType,
} from '../../../../modules/channelPartnerPrivateOffer/reducer'
import { PlanType } from '@labrav/react-components'
import { ChannelOfferInstallmentsView } from '../ChannelOfferInstallmentsView/ChannelOfferInstallmentsView'
import {
  TotalComponent,
  type TcvColumn,
} from '../../AddEditChannelOfferComponents/OfferMarkup/TotalComponent'
import { AdditionalUsageAndSubscriptionDimensions } from '../../AddEditChannelOfferComponents/AdditionalUsageAndSubscriptionDimensions/AdditionalUsageAndSubscriptionDimensions'
import { getMarkupValues } from '../../../../utils/channelOfferUtil'
import {
  getConditionalPriceLabel,
  getDimensionCustomColumnData,
  getPricesArrayForDimensions,
  getTableDimensions,
} from './helpers'

export const ChannelOfferDimensionDetails = ({
  channelOffer,
  resellerAuthorization,
  currencySymbol,
}: {
  channelOffer: ChannelPartnerPrivateOffer
  resellerAuthorization: ReceivedResellerAuthorization
  currencySymbol?: string
}) => {
  const { classes } = useStyles()
  const channelOfferDimensions = channelOffer?.dimensions

  const dimensionPriceArray = getPricesArrayForDimensions({
    channelOffer: channelOffer as ChannelPartnerPrivateOfferInCreation,
    resellerAuthorization,
  })

  const hasAdditionalUsageDimension =
    channelOfferDimensions?.some(
      dimension =>
        dimension?.isAdditionalUsageAllowed ||
        dimension.type === 'additional_usage'
    ) || false

  const isFlexiblePricingResaleAuth =
    resellerAuthorization?.discountType?.toUpperCase() ===
    DiscountType.FLEXIBLE_PAYMENT_TERMS

  const customContractDuration =
    resellerAuthorization?.contractDurationType === ContractDurationType.CUSTOM

  const additionalUsageDimensions = (channelOffer?.dimensions || []).filter(
    item => item?.isAdditionalUsageAllowed || item?.type === 'additional_usage'
  )

  const { totalDueToIsv, margin, markupOrTotal } = getMarkupValues({
    dimensions: channelOffer?.dimensions,
    adjustmentPercentage: channelOffer?.adjustmentPercentage || 0,
    priceAdjustmentType: channelOffer?.priceAdjustmentType,
    resaleAuthorization: resellerAuthorization,
  })

  const isMarkupPercentageSelected =
    channelOffer?.priceAdjustmentType === CppoPriceAdjustmentType.PERCENTAGE

  const tcvBuyer = isMarkupPercentageSelected
    ? markupOrTotal
    : channelOffer?.adjustmentPercentage

  const installmentsColumnData: TcvColumn[] = [
    {
      header: 'Total contract for buyer',
      isPrice: true,
      value: `${tcvBuyer}`,
    },
    {
      header: 'Total due to ISV',
      value: totalDueToIsv ? numberFormatWithPrecision(totalDueToIsv, 9) : '-',
      isPrice: true,
    },
    {
      header: 'Margin',
      value:
        margin !== '' && isNonEmptyNumber(totalDueToIsv)
          ? numberFormatWithPrecision(margin, 9)
          : '-',
      isPrice: true,
    },
  ]

  return (
    <div className={classes.cppoDimensionSection}>
      <SectionWrapper
        heading="Dimensions details"
        icon={faRuler}
        className={classes.dimensionBox}
        children={
          <div className={classes.dimensionWrapper}>
            {dimensionPriceArray?.map(priceItem => {
              return (
                <div
                  className={classes.rowWrapper}
                  data-testid={'dimension-table'}
                >
                  {resellerAuthorization?.discountType ===
                  DiscountType.FLEXIBLE_PAYMENT_TERMS ? (
                    <></>
                  ) : (
                    <div className={classes.tableSubHeading}>
                      {channelOffer?.flexiblePaymentTerms ||
                      resellerAuthorization?.productType ===
                        PlanType.SAAS_SUBSCRIPTION
                        ? ''
                        : `Pricing per dimension for ${getConditionalPriceLabel(
                            {
                              priceItem,
                              resellerAuthorization,
                            }
                          )} contract`}
                    </div>
                  )}
                  <StandardCustomDimensions
                    dimensions={
                      getTableDimensions({
                        priceItem,
                        channelOffer,
                        resellerAuthorization,
                      }) || []
                    }
                    customColumns={getDimensionCustomColumnData({
                      priceItem,
                      channelOffer,
                      resellerAuthorization,
                    })}
                    priceArray={[]}
                    priceColumnName={
                      channelOffer?.flexiblePaymentTerms ||
                      customContractDuration
                        ? ''
                        : 'prices'
                    }
                    currencySymbol={currencySymbol}
                  />
                </div>
              )
            })}
          </div>
        }
      />

      {(channelOffer?.flexiblePaymentTerms || isFlexiblePricingResaleAuth) && (
        <SectionWrapper
          heading={`Installments (${
            (channelOffer?.installments || []).length
          })`}
          icon={faRuler}
          children={
            <div
              className={classes.rowWrapper}
              data-testid="cppo-view-details-installments"
            >
              <div style={{ marginTop: '24px' }}>
                <TotalComponent
                  columns={installmentsColumnData}
                  currencySymbol={currencySymbol}
                />
              </div>
              <ChannelOfferInstallmentsView
                installments={channelOffer?.installments?.map(item => ({
                  ...item,
                  amount: item?.amount ? Number(item?.amount) : 0,
                }))}
                isvFlexiblePriceMarginPercentage={
                  isFlexiblePricingResaleAuth
                    ? (channelOffer?.adjustmentPercentage as number)
                    : undefined
                }
                currencySymbol={currencySymbol}
              />
            </div>
          }
        />
      )}

      {hasAdditionalUsageDimension && (
        <SectionWrapper
          heading="Price for additional usage"
          icon={faCalculator}
          children={
            <div
              className={classes.rowWrapper}
              data-testid="cppo-view-details-additional-usage-dims"
            >
              <div className={classes.tableSubHeading}>
                The unit price for each usage dimension is determined by
                applying the same markup ratio.
              </div>
              <AdditionalUsageAndSubscriptionDimensions
                dimensions={additionalUsageDimensions?.map(item => {
                  const resaleDimension = (
                    resellerAuthorization?.dimensions || []
                  )?.find(
                    resaleDimension =>
                      resaleDimension?.name === item?.name &&
                      resaleDimension?.isAdditionalUsageAllowed
                  )
                  const wholesalePrice = get(
                    resaleDimension?.prices,
                    'additionalUsagePrice',
                    0
                  )
                  const buyerPrice = get(
                    item?.prices,
                    'additionalUsagePrice',
                    0
                  )

                  return {
                    ...item,
                    wholesalePrice,
                    margin: buyerPrice ? buyerPrice - wholesalePrice : '',
                  }
                })}
                priceArray={[
                  {
                    keyName: 'wholesalePrice',
                    label: 'Wholesale unit price',
                  },
                  {
                    keyName: 'prices.additionalUsagePrice',
                    label: 'Buyer unit price',
                  },
                  {
                    keyName: 'margin',
                    label: 'Margin',
                  },
                ]}
                currencySymbol={currencySymbol}
              />
            </div>
          }
        />
      )}
    </div>
  )
}
